import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useI18n } from "../../i18n";
import { useAuth } from "../../context/AuthContext";
import { api, apiError } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Switch } from "../../components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { toast } from "sonner";

const TXNS = ["domestic", "exports", "imports", "gcc_transactions", "non_resident_services", "reverse_charge", "exempt_supplies", "government_contracts", "real_estate", "capital_assets", "credit_notes", "intercompany", "related_party"];

export default function Onboarding() {
  const { t } = useI18n();
  const { org, refreshOrg } = useAuth();
  const nav = useNavigate();
  const [f, setF] = useState({
    legal_name: org?.name || "", entity_type: "LLC", branches: 1, entities: 1, vat_group: false,
    annual_revenue: "", filing_frequency: "monthly", accounting_system: "", transactions: [], sector_type: "private", sector: org?.sector || "",
  });
  const [loading, setLoading] = useState(false);
  const [acts, setActs] = useState({});
  useEffect(() => { api.get("/sector-activities").then((r) => setActs(r.data)).catch(() => {}); }, []);
  const toggleTxn = (x) => setF((s) => ({ ...s, transactions: s.transactions.includes(x) ? s.transactions.filter((v) => v !== x) : [...s.transactions, x] }));

  const submit = async () => {
    setLoading(true);
    try {
      await api.put("/org/onboarding", f);
      await refreshOrg();
      toast.success(t("saved"));
      nav("/app");
    } catch (e) { toast.error(apiError(e)); } finally { setLoading(false); }
  };

  return (
    <div className="max-w-3xl mx-auto space-y-6 gc-fade-up">
      <div>
        <h1 className="font-display text-2xl font-bold text-[hsl(217_63%_15%)]">{t("onb_title")}</h1>
        <p className="text-muted-foreground mt-1">{t("onb_sub")}</p>
      </div>
      <div className="rounded-2xl border border-border bg-card p-6 space-y-5">
        <div className="grid sm:grid-cols-2 gap-4">
          <div><Label>{t("company_name")}</Label><Input data-testid="onb-name" className="mt-1.5" value={f.legal_name} onChange={(e) => setF({ ...f, legal_name: e.target.value })} /></div>
          <div><Label>{t("entity_type")}</Label><Input data-testid="onb-entity-type" className="mt-1.5" value={f.entity_type} onChange={(e) => setF({ ...f, entity_type: e.target.value })} /></div>
          <div className="sm:col-span-2">
            <Label className="mb-1.5 block">{t("sector_type")}</Label>
            <div className="flex flex-wrap gap-2">
              {["government", "private", "nonprofit"].map((s) => (
                <button key={s} type="button" data-testid={`onb-sector-type-${s}`} onClick={() => setF({ ...f, sector_type: s, sector: "" })}
                  className={`text-sm rounded-full px-3.5 py-1.5 border transition-colors ${f.sector_type === s ? "bg-[hsl(217_63%_15%)] text-white border-transparent" : "border-border hover:border-[hsl(158_64%_28%)]"}`}>
                  {t(`sec_${s}`)}
                </button>
              ))}
            </div>
            <p className="text-xs text-muted-foreground mt-2">{t(`sec_hint_${f.sector_type}`)}</p>
          </div>
          <div className="sm:col-span-2">
            <Label>{t("sector_activity")}</Label>
            <Select value={f.sector} onValueChange={(v) => setF({ ...f, sector: v })}>
              <SelectTrigger data-testid="onb-sector" className="mt-1.5"><SelectValue placeholder={t("select_ph")} /></SelectTrigger>
              <SelectContent className="max-h-60">{(acts[f.sector_type] || []).map((a) => <SelectItem key={a.en} value={a.en}>{a.ar}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div><Label>{t("branches")}</Label><Input data-testid="onb-branches" type="number" min={1} className="mt-1.5" value={f.branches} onChange={(e) => setF({ ...f, branches: parseInt(e.target.value || "1") })} /></div>
          <div><Label>{t("diag_entities")}</Label><Input data-testid="onb-entities" type="number" min={1} className="mt-1.5" value={f.entities} onChange={(e) => setF({ ...f, entities: parseInt(e.target.value || "1") })} /></div>
          <div>
            <Label>{t("filing_frequency")}</Label>
            <Select value={f.filing_frequency} onValueChange={(v) => setF({ ...f, filing_frequency: v })}>
              <SelectTrigger data-testid="onb-filing" className="mt-1.5"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="monthly">{t("monthly")}</SelectItem><SelectItem value="quarterly">{t("quarterly")}</SelectItem></SelectContent>
            </Select>
          </div>
          <div><Label>{t("annual_revenue")}</Label><Input data-testid="onb-revenue" className="mt-1.5" value={f.annual_revenue} onChange={(e) => setF({ ...f, annual_revenue: e.target.value })} /></div>
          <div className="sm:col-span-2"><Label>{t("accounting_system")}</Label><Input data-testid="onb-accounting" className="mt-1.5" value={f.accounting_system} onChange={(e) => setF({ ...f, accounting_system: e.target.value })} /></div>
        </div>
        <div className="flex items-center justify-between rounded-lg border border-border px-4 py-3">
          <Label htmlFor="vg">{t("vat_group")}</Label>
          <Switch id="vg" data-testid="onb-vat-group" checked={f.vat_group} onCheckedChange={(v) => setF({ ...f, vat_group: v })} />
        </div>
        <div>
          <Label className="mb-3 block">{t("diag_txn_q")}</Label>
          <div className="flex flex-wrap gap-2">
            {TXNS.map((x) => (
              <button key={x} data-testid={`onb-txn-${x}`} onClick={() => toggleTxn(x)}
                className={`text-sm rounded-full px-3.5 py-1.5 border transition-colors ${f.transactions.includes(x) ? "bg-[hsl(217_63%_15%)] text-white border-transparent" : "border-border hover:border-[hsl(158_64%_28%)]"}`}>
                {t(`txn_${x}`)}
              </button>
            ))}
          </div>
        </div>
        <Button data-testid="onb-submit" disabled={loading} onClick={submit} className="w-full rounded-full h-11 bg-[hsl(158_64%_28%)] hover:bg-[hsl(158_64%_24%)]">{loading ? t("loading") : t("save_profile")}</Button>
      </div>
    </div>
  );
}
